/**
 * useDefaultCharacter Hook
 *
 * Provides the default character for Photostudio auto-population.
 * Priority order:
 * 1. Most recently created reference person with images
 * 2. Default AI avatar (fallback if no reference persons exist)
 */

import { useMemo } from 'react';
import { useQuery } from 'convex/react';
import { api } from '../../convex/_generated/api';
import type { IDefaultCharacter } from '@/@types/photostudio';

export function useDefaultCharacter(): IDefaultCharacter | null {
  // Query for default reference person (most recently created with images)
  const defaultPerson = useQuery(api.referencePersons.getDefaultReferencePerson);

  // Query for default AI avatar (fallback)
  const defaultAvatar = useQuery(api.generatedAvatars.getDefaultAvatar);

  // Compute the default character with priority
  const defaultCharacter = useMemo((): IDefaultCharacter | null => {
    // Priority 1: Reference person with images
    if (defaultPerson && defaultPerson.imageUrls.length > 0) {
      return {
        type: 'reference-person',
        referencePersonId: defaultPerson._id,
        name: defaultPerson.name,
        imageUrl: defaultPerson.imageUrls[0],
        source: 'reference-person',
      };
    }

    // Priority 2: Default AI avatar (fallback)
    if (defaultAvatar && defaultAvatar.imageUrl && defaultAvatar.referencePersonId) {
      return {
        type: 'reference-person',
        referencePersonId: defaultAvatar.referencePersonId,
        name: defaultAvatar.referencePersonName || 'AI Avatar',
        imageUrl: defaultAvatar.imageUrl,
        source: 'ai-avatar',
      };
    }

    return null;
  }, [defaultPerson, defaultAvatar]);

  return defaultCharacter;
}
